// The per-minute rate line shown wherever a telehealth service is offered: `Reserver.jsx`'s
// chooser card, the times step's left rail and the booking summary (the tenant's own LIGHT
// `/reserver`), and the marketplace practitioner cards (`MarketplaceLearn.jsx`,
// `MarketplacePractitioner.jsx` — the platform's DARK storefront). Reads the catalogue's
// own `consult_pricing` snapshot straight off a SERVICE row (contract item 1 — the chooser
// row, or the `service` object GET /api/availability answers with; `consultPricing.js`'s
// `consultOf()` selects it before this component ever sees it) and states two things a
// per-minute buyer needs BEFORE they commit: the per-minute rate and the ceiling their card
// will be authorised for. Never a Stripe amount — this is advertised pricing, the same
// figures `ConsultCeilingDisclosure` restates from the server's own quote once the booking
// exists.
import React from "react";
import { useT } from "@/lib/LanguageContext";
import { fmtMoney } from "../admin/money";

// One class pair per surface, `LanguageSwitcher.jsx`'s `variant` convention. The light
// pair (`--eh-ink` #2C3531 / `--eh-muted` #4E574F) is the tenant's own cream `/reserver`;
// on the marketplace's dark chip (sampled rgb(33,36,52)) that same pair contrasts at
// 1.22:1 and 2.05:1 — the rate this component exists to publish was unreadable there.
// The dark pair reuses the tones the surrounding marketplace chips already use and
// contrasts at 15.4:1 / 7.4:1, both clear of WCAG AA's 4.5:1.
const TONES = {
  light: { amount: "text-eh-ink", ceiling: "text-eh-muted" },
  dark: { amount: "text-white", ceiling: "text-white/70" },
};

/**
 * @param {object} consult a `consult_pricing` snapshot ({rate_per_minute_cents,
 *   base_fee_cents, min_billable_minutes, max_billable_minutes, ceiling_cents, currency,
 *   no_show_fee_cents}) — callers only render this component once they hold one.
 * @param {string} [testId] `data-testid` for the wrapping element; each call site picks
 *   its own (service card, times-step rail, summary) so this component stays reusable.
 * @param {"light"|"dark"} [variant] the surface this label sits on. Defaults to "light"
 *   (the tenant's cream booking pages); dark surfaces must ask for "dark".
 */
export default function ConsultRateLabel({ consult, testId, variant = "light" }) {
  const t = useT("public");
  if (!consult) return null;
  const tone = TONES[variant] || TONES.light;
  return (
    <span className="inline-flex flex-col items-end leading-tight" data-testid={testId}>
      <span className={`font-medium text-xs ${tone.amount}`}>
        {t("consult.chooser.ratePerMinute", {
          amount: fmtMoney(consult.rate_per_minute_cents, consult.currency),
        })}
      </span>
      <span className={`text-[11px] ${tone.ceiling}`}>
        {t("consult.chooser.ceiling", {
          amount: fmtMoney(consult.ceiling_cents, consult.currency),
          max: consult.max_billable_minutes,
        })}
      </span>
    </span>
  );
}
